import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
} from "react-icons/fa";
import { fetchServicesPage } from "../api/servicesPage";
import { useHomePage } from "../context/HomePageContext";
import { logo, footerBg } from "../assets/appImages";

const QUICK_LINKS = [
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Awards", to: "/awards" },
  { label: "Blog & News", to: "/blog" },
];

const FOOTER_SERVICES = [
  { label: "Knee Pain", match: /knee/i },
  { label: "Hip Pain", match: /hip/i },
  { label: "Spine & Back Pain", match: /spine/i },
];

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/dryashgulatii/",
    icon: FaFacebookF,
    bg: "bg-[#1877F2]",
  },
  {
    label: "Twitter",
    href: "#",
    icon: FaTwitter,
    bg: "bg-[#1DA1F2]",
  },
  {
    label: "Instagram",
    href: "#",
    icon: FaInstagram,
    bg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
  },
  {
    label: "LinkedIn",
    href: "#",
    icon: FaLinkedinIn,
    bg: "bg-[#0077B5]",
  },
];

const Footer = () => {
  const { contact } = useHomePage();
  const [serviceLinks, setServiceLinks] = useState(
    FOOTER_SERVICES.map(({ label }) => ({ label, to: "/services" }))
  );

  useEffect(() => {
    let isMounted = true;

    const loadFooterServices = async () => {
      try {
        const { services } = await fetchServicesPage();
        if (!isMounted) return;

        setServiceLinks(
          FOOTER_SERVICES.map(({ label, match }) => {
            const service = services.find(({ title }) => match.test(title));
            return {
              label,
              to: service ? `/services/${service.id}` : "/services",
            };
          })
        );
      } catch {
        // Keep fallback links to /services
      }
    };

    loadFooterServices();

    return () => {
      isMounted = false;
    };
  }, []);
  // API logo is SVG (octet-stream/attachment) — browser <img> can't render it reliably
  const footerLogo =
    contact?.logo && !/\.svg($|\?)/i.test(contact.logo) ? contact.logo : logo;
  const footerDescription = contact?.description?.trim() || "";
  const hospitalLabel = "Hospital Visit Time";
  const hospitalHours = contact?.clinic_visit_time || "09:30 - 15:00";
  const clinicLabel = "Clinic Visit Time";
  const clinicHours = contact?.hospital_visit_time || "16:00 onward";

  return (
    <footer className="relative h-[500px] w-full overflow-hidden bg-white text-left">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] bg-white bg-cover bg-top bg-no-repeat "
        style={{
          backgroundImage: `url(${footerBg})`,
        }}
      />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col px-6 lg:px-10 pt-16">
        <div className="grid flex-1 grid-cols-1 gap-10 pb-4 pt-16 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8 lg:pt-20">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <img
                src={footerLogo}
                alt="Dr Yash Gulati"
                className="h-[58px] w-[200px] object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = logo;
                }}
              />
            </Link>
            {footerDescription && (
              <div
                className="mt-5 max-w-[280px] text-[15px] font-medium leading-[1.7] text-[#444444] [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: footerDescription }}
              />
            )}
          </div>

          <div>
            <h3 className="text-[24px] font-bold text-[#760982]">Quick Links</h3>
            <ul className="mt-8 space-y-3">
              {QUICK_LINKS.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-[16px] font-medium text-[#444444] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[24px] font-bold text-[#760982]">Our Service</h3>
            <ul className="mt-8 space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-[16px] font-medium text-[#444444] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[24px] font-bold text-[#760982]">Open Hours</h3>
            <div className="mt-5 space-y-5">
              <div>
                <p className="text-[14px] font-semibold text-[#F17732]">{hospitalLabel}</p>
                <p className="mt-1 text-[20px] font-bold text-[#760982]">{hospitalHours}</p>
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[#F17732]">{clinicLabel}</p>
                <p className="mt-1 text-[20px] font-bold text-[#760982]">{clinicHours}</p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon, bg }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex h-[30px] w-[30px] items-center justify-center rounded-[4px] text-[13px] text-white transition-opacity hover:opacity-90 ${bg}`}
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-[#D4D5F1] py-4 text-center">
          <p className="text-[14px] text-[#444444]">
            Copyright © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
